import React from "react";
import {Chip, TextField, TextFieldProps} from "@mui/material";

export interface ChipInputProps extends Omit<TextFieldProps, "value" | "onChange"> {
    values: string[];
    onChange: (values: string[], input: string) => void;
    inputValue: string;
    maxVisibleChips?: number;
}

export default function ChipInput({values, onChange, inputValue, maxVisibleChips, ...rest}: Readonly<ChipInputProps>) {
    const handleChangeInputValue = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const input = evt.target.value;
        const regex = /[\w-]+(?=[\s,;|])/g;
        const newChips: string[] = input.match(regex) || [];
        const newInputValue = input
            .replace(regex, "")
            .replace(/[\s,;|]/g, "")
            .trimStart();
        onChange([...values, ...newChips], newInputValue); //trim removed because regex does not capture whitespaces
    };
    const handleDelete = (index: number) => () => {
        const newValues = [...values];
        newValues.splice(index, 1);
        onChange(newValues, inputValue);
    };

    const handleSubmit = () => {
        if (inputValue && inputValue.trim().length > 0) {
            onChange([...values, inputValue.trim()], "");
        }
    };

    const handleKeyDown = (evt: React.KeyboardEvent) => {
        if (evt.key === "Enter") {
            handleSubmit();
        }
        if (evt.key === "Backspace" && inputValue.length === 0 && values.length > 0) {
            handleDelete(values.length - 1)();
        }
        if (evt.key === "Escape") {
            onChange(values, "");
        }
    };

    const visibleValues = maxVisibleChips && values.length > maxVisibleChips ? values.slice(-maxVisibleChips) : values;
    const startAdornment = visibleValues.map((chipValue, chipIndex) => (
        <Chip
            key={chipIndex}
            tabIndex={-1}
            label={chipValue}
            onDelete={handleDelete(
                maxVisibleChips && values.length > maxVisibleChips
                    ? values.length - maxVisibleChips + chipIndex
                    : chipIndex
            )}
        />
    ));
    if (maxVisibleChips && values.length > maxVisibleChips) {
        const additionalChips = `+${values.length - maxVisibleChips}`;
        startAdornment.unshift(<Chip key={additionalChips} tabIndex={-1} label={additionalChips} />);
    }

    return (
        <TextField
            InputProps={{
                startAdornment: startAdornment,
                endAdornment: values.length ? <Chip label={"All"} onDelete={() => onChange([], "")} /> : undefined,
            }}
            value={inputValue}
            onChange={handleChangeInputValue}
            onBlur={handleSubmit}
            onKeyDown={handleKeyDown}
            {...rest}
        />
    );
}
